// Layer 3 — Detail view (occupational health).
// Answers "Why is it happening?" — cohort-level trend analysis and
// individual-level data (access controlled).

import React, { useState } from "react";
import {
  Box,
  Chip,
  Grid,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import PsychologyAltRoundedIcon from "@mui/icons-material/PsychologyAltRounded";
import VerifiedUserRoundedIcon from "@mui/icons-material/VerifiedUserRounded";
import { LineChart } from "../../../../../shared/components/charts";
import {
  GlassCard,
  METRIC_ACCENTS,
  PersonDial,
  SectionHeading,
  Sparkline,
  TrafficLightCell,
  tierColor,
} from "./primitives";
import { accessControls, cohortDetail, individuals } from "../data/mockData";

export const DetailView: React.FC = () => {
  const theme = useTheme();
  const [selectedId, setSelectedId] = useState(individuals[0].id);
  const person = individuals.find((p) => p.id === selectedId) ?? individuals[0];

  const personMetricCards = [
    { code: "FED", value: person.metrics.fed, accent: METRIC_ACCENTS.fed },
    { code: "LRM", value: person.metrics.lrm, accent: METRIC_ACCENTS.lrm },
    { code: "CDI", value: person.metrics.cdi, accent: METRIC_ACCENTS.cdi },
    { code: "PDC", value: person.metrics.pdc, accent: METRIC_ACCENTS.pdc },
  ];

  return (
    <Stack spacing={3}>
      <SectionHeading
        overline="Detail view · Occupational health"
        title="Why is it happening?"
        subtitle="Cohort-level trend analysis and individual-level data (access controlled)."
      />

      {/* ── Individual tracking (OH only) ───────────────────────────────── */}
      <GlassCard glow accent={tierColor(person.tier, theme)}>
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          sx={{ mb: 1 }}
        >
          <SectionHeading
            overline="Individual tracking · clinician access"
            title="Per-worker physiological record"
          />
          <Chip
            icon={<VerifiedUserRoundedIcon fontSize="small" />}
            label={`Consent ${person.consent} · access logged`}
            size="small"
            sx={{
              fontWeight: 700,
              bgcolor: alpha(theme.palette.success.main, 0.12),
              color: "success.main",
            }}
          />
        </Stack>

        {/* pseudonymised worker selector */}
        <ToggleButtonGroup
          exclusive
          size="small"
          value={selectedId}
          onChange={(_, v) => v && setSelectedId(v)}
          sx={{ mb: 2, flexWrap: "wrap" }}
        >
          {individuals.map((p) => (
            <ToggleButton key={p.id} value={p.id} sx={{ textTransform: "none" }}>
              <Stack alignItems="flex-start" sx={{ px: 0.5 }}>
                <Typography variant="body2" fontWeight={700}>
                  {p.alias}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {p.cohort}
                </Typography>
              </Stack>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <Grid container spacing={3} alignItems="center">
          {/* dial + identity */}
          <Grid size={{ xs: 12, md: 3 }}>
            <Stack alignItems="center" spacing={1}>
              <PersonDial score={person.fatigueScore} tier={person.tier} />
              <Typography variant="subtitle1" fontWeight={800}>
                {person.alias}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {person.role}
              </Typography>
            </Stack>
          </Grid>

          {/* per-person metric small multiples */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Grid container spacing={1.5}>
              {personMetricCards.map((m) => (
                <Grid size={{ xs: 6 }} key={m.code}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      borderLeft: `3px solid ${m.accent}`,
                      bgcolor: alpha(m.accent, 0.06),
                    }}
                  >
                    <Typography
                      variant="caption"
                      fontWeight={800}
                      sx={{ color: m.accent, letterSpacing: 1 }}
                    >
                      {m.code}
                    </Typography>
                    <Typography variant="h5" fontWeight={800}>
                      {m.value}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* vitals summary */}
          <Grid size={{ xs: 12, md: 5 }}>
            <Stack spacing={1.5}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.text.primary, 0.03),
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Avg sleep vs need
                    </Typography>
                    <Typography variant="h6" fontWeight={800}>
                      {person.avgSleep}h{" "}
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.secondary"
                      >
                        / {person.sleepNeed}h
                      </Typography>
                    </Typography>
                  </Box>
                  <Sparkline
                    values={person.series.map((s) => s.actual)}
                    color={theme.palette.primary.main}
                  />
                </Stack>
              </Box>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.text.primary, 0.03),
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      HRV (overnight)
                    </Typography>
                    <Typography variant="h6" fontWeight={800}>
                      {person.hrv}ms
                    </Typography>
                  </Box>
                  <Sparkline
                    values={person.series.map((s) => -s.hrvZ)}
                    color={METRIC_ACCENTS.pdc}
                  />
                </Stack>
              </Box>
            </Stack>
          </Grid>
        </Grid>

        <Grid container spacing={3} sx={{ mt: 0.5 }}>
          {/* sleep-debt accumulation */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" fontWeight={800} gutterBottom>
              Sleep debt — need vs actual (14 days)
            </Typography>
            <LineChart
              data={person.series}
              xAxisKey="day"
              height={220}
              series={[
                {
                  dataKey: "need",
                  label: "Need",
                  color: theme.palette.text.secondary,
                  dashed: true,
                },
                {
                  dataKey: "actual",
                  label: "Actual sleep",
                  color: theme.palette.primary.main,
                  area: true,
                },
              ]}
            />
          </Grid>

          {/* baseline-deviation strip (PDC z-scores) */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" fontWeight={800} gutterBottom>
              Vital deviation from personal baseline (z-score)
            </Typography>
            <LineChart
              data={person.series}
              xAxisKey="day"
              height={220}
              series={[
                { dataKey: "hrvZ", label: "HRV", color: METRIC_ACCENTS.pdc },
                { dataKey: "rhrZ", label: "Resting HR", color: METRIC_ACCENTS.fed },
                { dataKey: "respZ", label: "Resp rate", color: METRIC_ACCENTS.lrm },
                { dataKey: "skinTempZ", label: "Skin temp", color: METRIC_ACCENTS.cdi },
              ]}
            />
            <Typography variant="caption" color="text.secondary">
              All signals compared to the individual's own 30-day baseline — never
              population norms. 2+ simultaneously flagged = PDC day.
            </Typography>
          </Grid>
        </Grid>
      </GlassCard>

      <GlassCard glow>
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
        >
          <Box>
            <Typography variant="h5" fontWeight={800}>
              {cohortDetail.cohort}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {cohortDetail.window}
            </Typography>
          </Box>
          <Chip
            icon={<LockRoundedIcon fontSize="small" />}
            label="Access controlled · audited"
            size="small"
            sx={{
              fontWeight: 700,
              bgcolor: alpha(theme.palette.info.main, 0.12),
              color: "info.main",
            }}
          />
        </Stack>

        <Grid container spacing={2} sx={{ mt: 1 }}>
          {cohortDetail.metrics.map((m) => {
            const color = tierColor(m.tier, theme);
            return (
              <Grid size={{ xs: 6, sm: 4, md: 2 }} key={m.label}>
                <Box
                  sx={{
                    p: 1.5,
                    height: "100%",
                    borderRadius: 2,
                    textAlign: "center",
                    background: `linear-gradient(160deg, ${alpha(
                      color,
                      0.14
                    )}, ${alpha(color, 0.03)})`,
                    border: `1px solid ${alpha(color, 0.3)}`,
                  }}
                >
                  <Typography variant="h5" fontWeight={800} sx={{ color }}>
                    {m.value}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {m.label}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color, fontWeight: 700, display: "block", mt: 0.5 }}
                  >
                    {m.delta}
                  </Typography>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </GlassCard>

      <GlassCard>
        <SectionHeading title="14-day LRM daily status" subtitle="Most recent on the right" />
        <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap", gap: 1 }}>
          {cohortDetail.lrm14.map((tier, i) => (
            <TrafficLightCell key={i} tier={tier} />
          ))}
        </Stack>
      </GlassCard>

      {/* Auto-generated insight */}
      <Box
        sx={{
          position: "relative",
          borderRadius: 3,
          p: 2.5,
          overflow: "hidden",
          background: `linear-gradient(120deg, ${alpha(
            theme.palette.primary.main,
            0.14
          )}, ${alpha(theme.palette.secondary.main, 0.1)})`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <PsychologyAltRoundedIcon sx={{ color: "primary.main" }} />
          <Box>
            <Typography
              variant="overline"
              sx={{ color: "primary.main", fontWeight: 700 }}
            >
              Auto-generated insight
            </Typography>
            <Typography variant="body1" sx={{ mt: 0.5 }}>
              {cohortDetail.insight}
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* Access & privacy controls */}
      <GlassCard accent={theme.palette.info.main}>
        <SectionHeading
          title="Access & privacy controls"
          subtitle="UK GDPR — biometric data is special category. Individual access logged and audited."
        />
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {accessControls.map((a) => (
            <Grid size={{ xs: 12, sm: 6 }} key={a.role}>
              <Stack
                direction="row"
                spacing={1.5}
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  height: "100%",
                  bgcolor: alpha(theme.palette.info.main, 0.06),
                }}
              >
                <LockRoundedIcon fontSize="small" sx={{ color: "info.main", mt: 0.3 }} />
                <Box>
                  <Typography variant="subtitle2" fontWeight={800}>
                    {a.role}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {a.access}
                  </Typography>
                </Box>
              </Stack>
            </Grid>
          ))}
        </Grid>
      </GlassCard>
    </Stack>
  );
};
